import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../App';
import { toast } from 'react-toastify';

function MatchRegistrationsList({ match, session, onClose }) {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRegistrations();
  }, [match.id]);

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const token = session?.access_token || localStorage.getItem('admin_token');

      const response = await fetch(`${API_BASE}/matches/${match.id}/registrations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRegistrations(data);
      } else {
        throw new Error('Failed to fetch registrations');
      }
    } catch (error) {
      console.error('Error fetching registrations:', error);
      toast.error('Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['#', 'User ID', 'BGMI Username / Team Name', 'Team Members', 'Slot', 'Paid', 'Joined At'];
    const rows = registrations.map((reg, index) => [
      index + 1,
      reg.user_id,
      reg.team_name || reg.bgm_username || 'N/A',
      reg.team_members ? reg.team_members.join(', ') : 'Solo',
      reg.assigned_slot || 'N/A',
      reg.paid ? 'Yes' : 'No',
      new Date(reg.joined_at).toLocaleString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${match.title}_registrations.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast.success('CSV exported successfully!');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '900px', maxHeight: '90vh' }}
      >
        <div className="modal-header">
          <div>
            <h3 className="modal-title">Match Registrations</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '5px' }}>
              {match.title}
            </p>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div>
          {/* Stats */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '15px',
            marginBottom: '20px'
          }}>
            <div style={{
              padding: '15px',
              background: 'var(--accent-bg)',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Total Registrations
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--primary-color)' }}>
                {registrations.length}
              </div>
            </div>
            <div style={{
              padding: '15px',
              background: 'var(--accent-bg)',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Paid
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--success-color)' }}>
                {registrations.filter(r => r.paid).length}
              </div>
            </div>
            <div style={{
              padding: '15px',
              background: 'var(--accent-bg)',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Slots Available
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--warning-color)' }}>
                {match.slot_count - registrations.length}
              </div>
            </div>
          </div>

          {/* Export Button */}
          <div style={{ marginBottom: '20px' }}>
            <button className="btn btn-primary" onClick={exportToCSV}>
              📥 Export to CSV
            </button>
          </div>

          {/* Registrations List */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
              <p style={{ marginTop: '20px', color: 'var(--text-secondary)' }}>
                Loading registrations...
              </p>
            </div>
          ) : registrations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
              <p style={{ fontSize: '3rem', marginBottom: '15px' }}>📋</p>
              <p>No registrations yet</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User ID</th>
                    <th>BGMI Username / Team Name</th>
                    <th>Team Members</th>
                    <th>Slot</th>
                    <th>Joined At</th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.map((reg, index) => (
                    <tr key={reg.id}>
                      <td>{index + 1}</td>
                      <td style={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>
                        {reg.user_id.slice(0, 8)}...
                      </td>
                      <td>
                        {reg.team_name ? (
                          <span style={{ fontWeight: 'bold' }}>{reg.team_name}</span>
                        ) : (
                          reg.bgm_username || <em style={{ color: 'var(--text-secondary)' }}>N/A</em>
                        )}
                      </td>
                      <td>
                        {reg.team_members && reg.team_members.length > 0 ? (
                          <div style={{ fontSize: '0.85rem' }}>
                            {reg.team_members.map((member, i) => (
                              <div key={i}>• {member}</div>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)' }}>Solo</span>
                        )}
                      </td>
                      <td>
                        <div style={{
                          fontWeight: 'bold',
                          fontSize: '1.1rem',
                          color: 'var(--primary-color)'
                        }}>
                          {reg.assigned_slot ? `#${reg.assigned_slot}` : <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>-</span>}
                        </div>
                      </td>
                      <td style={{ fontSize: '0.85rem' }}>
                        <div>{new Date(reg.joined_at).toLocaleDateString()}</div>
                        <div style={{ color: 'var(--text-secondary)' }}>
                          {new Date(reg.joined_at).toLocaleTimeString()}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MatchRegistrationsList;
